import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from './supabase';
import { heartGet, heartSetOn, heartSubscribe, heartSetInCall } from './heart';   // [heart]
import { earPause, earResume } from './ear';   // [ear-chat] 通话页用自己那路麦，全局耳朵先让路

function VoiceCall(props) {   // [call-mini] props: inviteOverride / onEnd / onMinimize（由 App.js 的 CallHost 传）
  props = props || {};
  var navigate = useNavigate();
  var location = useLocation();
  var _vcQ = new URLSearchParams(window.location.search);
  var inviteFromState = props.inviteOverride || (location.state && location.state.invite) || (_vcQ.get('invite_id') ? { id: parseInt(_vcQ.get('invite_id'), 10) || _vcQ.get('invite_id'), reason: _vcQ.get('reason') || '' } : null);
  var openingDoneRef = useRef(false);
  var [pendingAudio, setPendingAudio] = useState(null); // autoplay被拦时的手动播放兜底
  var [expandedCaps, setExpandedCaps] = useState({}); // 点英文展开中文字幕
  var [status, setStatus] = useState('idle');
  var [transcript, setTranscript] = useState('');
  var [reply, setReply] = useState('');
  var [elapsed, setElapsed] = useState(0);
  var [convoId, setConvoId] = useState(null);
  var [avatar, setAvatar] = useState(null);
  var [avatarHer, setAvatarHer] = useState(null);
  var [nickname, setNickname] = useState('\u6797\u664F');
  var [callLog, setCallLog] = useState([]);
  var [bgImage, setBgImage] = useState(function() { try { return localStorage.getItem('chat_bg_url') || null; } catch(e) { return null; } });
  var [nicknameHer, setNicknameHer] = useState('\u77E5\u679D');
  var mediaRecorder = useRef(null);
  var audioChunks = useRef([]);
  var timerRef = useRef(null);
  var audioRef = useRef(null);
  var startTimeRef = useRef(null);
  var captionsRef = useRef(null);
  var callLogRef = useRef([]);
  // linger状态镜像ref：startRecording被useCallback([])冻结在第一次渲染，读state永远是false，必须读ref
  var lingerActiveRef = useRef(false);

  // ========== [ear] 通话里的耳朵：常开一路麦，原始音频递给原生壳，壳认出声音回标签 ==========
  var earNative = !!(window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.nativeAction);
  var [earOn, setEarOn] = useState(function() { try { return earNative && localStorage.getItem('ear_enabled') === 'true'; } catch(e) { return false; } });
  var [earLast, setEarLast] = useState('');          // 屏幕上一闪而过的"他听到了…"
  var earOnRef = useRef(earOn);
  var earStreamRef = useRef(null);
  var earCtxRef = useRef(null);
  var earPendingRef = useRef([]);                    // [{cn, at}]
  var earRingRef = useRef({ chunks: [], len: 0 });
  var earTakenClipsRef = useRef([]);   // [ear-clip] 最近 4 秒的原始音频，认出声音时截 3.5 秒存起来
  var earSeenRef = useRef({});                       // cn -> 上次时间（8 秒内同种只记一次）
  var lastTurnAtRef = useRef(Date.now());
  var lastAmbientAtRef = useRef(0);
  var statusRef = useRef('idle');
  var earPost = function(obj) { try { window.webkit.messageHandlers.nativeAction.postMessage(obj); } catch(e) {} };
  var runBt = function(d) { if (d && Array.isArray(d.bt_commands)) d.bt_commands.forEach(function(c) { earPost(c); }); };   // [call-tools] 他在电话里动玩具
  // ========== [handsfree] 免按键：常开那路麦自己切句（能量 VAD），停顿 1.6 秒就当她说完一句，转写后：是话就当话，是"嗯/啊/呜"就当呻吟标签 ==========
  var [handsFree, setHandsFree] = useState(function() { try { return localStorage.getItem('call_handsfree') === 'true'; } catch(e) { return false; } });
  var handsFreeRef = useRef(handsFree);
  var segRef = useRef({ active: false, chunks: [], pre: [], len: 0, startAt: 0, silence: 0, run: 0 });
  var noiseRef = useRef(0.004);          // 环境底噪（慢慢学）
  var interruptRunRef = useRef(0);
  var hfBusyRef = useRef(false);          // 正在转写/等他回
  var hfQueueRef = useRef(null);          // 他在想的时候她又说了一句：先存着
  var HF_SILENCE_MS = 1600, HF_MIN_SAMPLES = 8000, HF_MAX_SAMPLES = 16000 * 20, HF_PRE_CHUNKS = 4;
  useEffect(function() { handsFreeRef.current = handsFree; try { localStorage.setItem('call_handsfree', handsFree ? 'true' : 'false'); } catch(e) {} }, [handsFree]);
  var wavBlob = function(chunks, len) {   // 16k 单声道 Int16 → WAV
    var buf = new ArrayBuffer(44 + len * 2), v = new DataView(buf), o = 0;
    var wstr = function(str) { for (var i = 0; i < str.length; i++) v.setUint8(o++, str.charCodeAt(i)); };
    wstr('RIFF'); v.setUint32(o, 36 + len * 2, true); o += 4; wstr('WAVE'); wstr('fmt ');
    v.setUint32(o, 16, true); o += 4; v.setUint16(o, 1, true); o += 2; v.setUint16(o, 1, true); o += 2;
    v.setUint32(o, 16000, true); o += 4; v.setUint32(o, 32000, true); o += 4; v.setUint16(o, 2, true); o += 2; v.setUint16(o, 16, true); o += 2;
    wstr('data'); v.setUint32(o, len * 2, true); o += 4;
    var out = new Int16Array(buf, 44); var p = 0;
    chunks.forEach(function(c) { out.set(c, p); p += c.length; });
    return new Blob([buf], { type: 'audio/wav' });
  };
  // 语气词 → 标签（她那边的分法：嗯哼唔呃→呻吟，呜→呜咽，啊哈呀→叫出声）；识别器的幻觉句子当没听见
  var HALLU = /谢谢观看|点赞|订阅|字幕|明镜|thank you for watching|subtitles|amara\.org|^thank you\.?$|^you\.?$/i;
  var fillerTag = function(text) {
    var t = String(text || '').replace(/[\s　,.!?。，！？~～…、—\-'"“”‘’()（）]/g, '');
    if (!t) return { tag: null, filler: true };
    if (HALLU.test(text)) return { tag: null, filler: true };
    if (!/^[嗯哼唔呃呜啊哈呀哦噢嘶欸诶哎唉呐嘛mhuaoe]+$/i.test(t)) return { tag: null, filler: false };
    if (/[呜]|^w/i.test(t)) return { tag: 'whimper', cn: '呬咽', filler: true };
    if (/[啊哈呀]|^a+h*$|^ah/i.test(t)) return { tag: 'cry_out', cn: '叫出声', filler: true };
    if (/[嗯哼唔呃]|^[mh]+$|^u+h*$|^hm/i.test(t)) return { tag: 'moan', cn: '轻轻的呦吟', filler: true };
    return { tag: null, filler: true };
  };
  // [ear-clip] 截最近 3.5 秒 → wav → 传到 storage（她自己的桶），回 url
  var earClip = function() {
    try {
      var ring = earRingRef.current; if (!ring.len) return Promise.resolve(null);
      var want = 56000, chunks = [], len = 0;
      for (var i = ring.chunks.length - 1; i >= 0 && len < want; i--) { chunks.unshift(ring.chunks[i]); len += ring.chunks[i].length; }
      return uploadAudio(wavBlob(chunks, len), 'ear');
    } catch(e) { return Promise.resolve(null); }
  };
  var pushEar = function(tag, cn) {
    var now = Date.now();   // 同一种声音 8 秒内只记一次（按 tag 算，后面挂的"急促/比刚才响"几个字不算新声音）
    if (earSeenRef.current[tag] && now - earSeenRef.current[tag] < 8000) return;
    earSeenRef.current[tag] = now;
    earPendingRef.current.push({ cn: cn, tag: tag, at: now, clip: tag === 'heart' ? Promise.resolve(null) : earClip() });   // [ear-clip]
    setEarLast(cn);
    setTimeout(function() { setEarLast(function(v) { return v === cn ? '' : v; }); }, 3500);
  };
  // ========== [heart] 手环心跳：全局模块（heart.js）管连接和快慢，这里只订阅：屏幕显示 + 当标签给他 ==========
  var [heartOn, setHeartOn] = useState(function() { return heartGet().on; });
  var [heartState, setHeartState] = useState(function() { return heartGet().state; });
  var [bpmNow, setBpmNow] = useState(function() { return heartGet().bpm; });
  useEffect(function() { earPause(); return function() { earResume(); }; }, []);   // [ear-chat]
  useEffect(function() {
    heartSetInCall(true);
    var off = heartSubscribe(function(ev) {
      if (ev.type === 'state') { setHeartState(ev.state); if (ev.state === 'off') setBpmNow(0); }
      else if (ev.type === 'bpm') { setBpmNow(ev.bpm); setHeartState('on'); }
      else if (ev.type === 'report') { pushEar('heart', ev.cn); }
    });
    return function() { off(); heartSetInCall(false); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  var toggleHeart = function() { var on = !heartOn; setHeartOn(on); heartSetOn(on); if (!on) { setHeartState('off'); setBpmNow(0); } };
  var earTake = function() {   // 这一轮带走的标签：去重、最多 2 个、按时间
    var now = Date.now();
    var list = earPendingRef.current.filter(function(t) { return now - t.at < 20000; });
    earPendingRef.current = [];
    var byTag = {}, order = [], clips = {};
    list.forEach(function(t) { var k = t.tag || t.cn; if (!byTag[k]) order.push(k); byTag[k] = t.cn; if (t.clip) clips[k] = t.clip; });   // 同种只留最新那条描述
    var keys = order.slice(-2);
    earTakenClipsRef.current = keys.map(function(k) { return clips[k]; }).filter(Boolean);   // [ear-clip]
    return keys.map(function(k) { return byTag[k]; });
  };
  useEffect(function() { statusRef.current = status; window.__aizhiCallStatus = status; }, [status]);   // [call-mini]
  // [ear-log] 他听到的也要留痕：进通话记录（居中一行小字），也进聊天记录，挂了以后在 chat 里看得到、他之后也记得
  var earLog = function(tags) {
    if (!tags || !tags.length) return;
    var text = '\u3014\u901A\u8BDD\u91CC\u4ED6\u542C\u5230\uFF1A' + tags.join('\u3001') + '\u3015';   // 〔通话里他听到：喘息、叹气〕
    var entry = { role: 'ear', text: text, time: new Date().toISOString(), audio_url: null };
    callLogRef.current = callLogRef.current.concat([entry]);
    setCallLog(function(prev) { return prev.concat([entry]); });
    var clips = earTakenClipsRef.current; earTakenClipsRef.current = [];
    Promise.all(clips).then(function(urls) {   // [ear-clip] 录音传好了再落库，带着 url
      var url = (urls || []).filter(Boolean)[0] || null;
      entry.audio_url = url;
      setCallLog(function(prev) { return prev.slice(); });
      if (convoId) supabase.from('messages').insert([{ conversation_id: convoId, sender: 'zhizhi', text: text, audio_url: url, created_at: new Date().toISOString() }]).then(function() {}, function() {});
    });
  };
  useEffect(function() {
    earOnRef.current = earOn;
    try { localStorage.setItem('ear_enabled', earOn ? 'true' : 'false'); } catch(e) {}
    // 开关变了就先把旧的那路关掉，再按需要重开（耳朵和免按键共用这一路麦）
    if (earNative) earPost({ action: 'ear_state', enabled: false });
    if (earCtxRef.current) { try { earCtxRef.current.close(); } catch(e) {} earCtxRef.current = null; }
    if (earStreamRef.current) { earStreamRef.current.getTracks().forEach(function(t) { t.stop(); }); earStreamRef.current = null; }
    segRef.current.active = false; segRef.current.chunks = []; segRef.current.pre = [];
    if ((!earOn || !earNative) && !handsFree) return;   // [handsfree] 免按键也要这路常开麦
    var cancelled = false;
    (async function() {
      try {
        var stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: false, autoGainControl: false } });
        if (cancelled) { stream.getTracks().forEach(function(t) { t.stop(); }); return; }
        earStreamRef.current = stream;
        var AC = window.AudioContext || window.webkitAudioContext;
        var ctx = new AC();
        earCtxRef.current = ctx;
        var src = ctx.createMediaStreamSource(stream);
        var proc = ctx.createScriptProcessor(4096, 1, 1);
        var ratio = ctx.sampleRate / 16000;           // iOS 一般 48000 → 3
        var acc = [], accLen = 0;
        proc.onaudioprocess = function(e) {
          if (!earOnRef.current && !handsFreeRef.current) return;
          var inp = e.inputBuffer.getChannelData(0);
          var outN = Math.floor(inp.length / ratio);
          var out = new Int16Array(outN);
          for (var i = 0; i < outN; i++) {           // 简单抽取+小平均
            var p = i * ratio, j = Math.floor(p), v = (inp[j] + (inp[j + 1] || inp[j])) * 0.5;
            out[i] = Math.max(-32768, Math.min(32767, Math.round(v * 32767)));
          }
          var ring = earRingRef.current; ring.chunks.push(out); ring.len += out.length;   // [ear-clip]
          while (ring.len > 64000 && ring.chunks.length > 1) { ring.len -= ring.chunks[0].length; ring.chunks.shift(); }
          if (handsFreeRef.current) vadRef.current(inp, out);   // [handsfree]
          if (!earOnRef.current || !earNative) return;
          acc.push(out); accLen += outN;
          if (accLen >= 8000) {                      // 攒够半秒再递
            var all = new Int16Array(accLen), o = 0;
            acc.forEach(function(a) { all.set(a, o); o += a.length; });
            acc = []; accLen = 0;
            var bytes = new Uint8Array(all.buffer), bin = '';
            for (var k = 0; k < bytes.length; k += 8192) bin += String.fromCharCode.apply(null, bytes.subarray(k, k + 8192));
            earPost({ action: 'ear_pcm', b64: btoa(bin) });
          }
        };
        src.connect(proc); proc.connect(ctx.destination);   // 不接 destination 在 iOS 上不跑
        if (earOn && earNative) earPost({ action: 'ear_state', enabled: true, talking: false });
      } catch(e) { console.log('[ear] 开麦失败', e); setEarOn(false); setHandsFree(false); }
    })();
    return function() { cancelled = true; };
  }, [earOn, handsFree]); // eslint-disable-line react-hooks/exhaustive-deps

  // [handsfree] 能量 VAD：一块约 85ms。有声连着 2 块开始一句（带前面 4 块），没声 1.6 秒结束；他在说话时她得明显大声才算抢话
  var vad = function(inp, out16) {
    var sum = 0; for (var i = 0; i < inp.length; i++) sum += inp[i] * inp[i];
    var rms = Math.sqrt(sum / inp.length);
    var nf = noiseRef.current;
    noiseRef.current = rms < nf ? rms : nf + (rms - nf) * 0.004;
    var thresh = Math.max(0.012, noiseRef.current * 4);
    var seg = segRef.current;
    var st = statusRef.current;
    var blockMs = inp.length / (earCtxRef.current ? earCtxRef.current.sampleRate : 48000) * 1000;
    if (!seg.active) {
      seg.pre.push(out16); if (seg.pre.length > HF_PRE_CHUNKS) seg.pre.shift();
      if (st === 'speaking') {                     // 抢话：要比平时大得多，而且连着 4 块
        if (rms > Math.max(0.05, noiseRef.current * 10)) interruptRunRef.current += 1; else interruptRunRef.current = 0;
        if (interruptRunRef.current >= 4) {
          interruptRunRef.current = 0;
          if (audioRef.current) { try { audioRef.current.pause(); } catch(e) {} }
          setStatus('idle'); statusRef.current = 'idle';
        } else return;
      }
      if (st === 'thinking' && hfQueueRef.current) return;   // 他在想，而且已经排了一句：别再攒
      seg.run = rms > thresh ? seg.run + 1 : 0;
      if (seg.run >= 2) {
        seg.active = true; seg.chunks = seg.pre.slice(); seg.len = 0;
        seg.chunks.forEach(function(c) { seg.len += c.length; });
        seg.startAt = Date.now() - HF_PRE_CHUNKS * blockMs; seg.silence = 0; seg.pre = [];
        setStatus(function(x) { return x === 'idle' ? 'listening' : x; });
      }
      return;
    }
    seg.chunks.push(out16); seg.len += out16.length;
    seg.silence = rms > thresh ? 0 : seg.silence + blockMs;
    if (seg.silence >= HF_SILENCE_MS || seg.len >= HF_MAX_SAMPLES) {
      var chunks = seg.chunks, len = seg.len, startAt = seg.startAt;
      seg.active = false; seg.chunks = []; seg.len = 0; seg.run = 0;
      setStatus(function(x) { return x === 'listening' ? 'idle' : x; });
      if (len - HF_PRE_CHUNKS * out16.length < HF_MIN_SAMPLES) return;   // 太短：咳一下、碰一下
      var item = { blob: wavBlob(chunks, len), startAt: startAt, endAt: Date.now() };
      if (hfBusyRef.current) { hfQueueRef.current = item; return; }
      runUtterance(item);
    }
  };
  var vadRef = useRef(null); vadRef.current = vad;
  var runUtterance = async function(item) {
    hfBusyRef.current = true;
    try { await processUtterance(item.blob, 'wav', item.startAt, item.endAt); } catch(e) {}
    hfBusyRef.current = false;
    var q = hfQueueRef.current; hfQueueRef.current = null;
    if (q && handsFreeRef.current) runUtterance(q);
  };
  // 壳回标签：1.2 秒内合并靠 earTake 的去重，这里只做 8 秒同种去重
  useEffect(function() {
    window.aizhiEar = function(t) {
      if (!t || !t.cn || !earOnRef.current) return;
      pushEar(t.tag || t.cn, t.cn);
    };
    return function() { window.aizhiEar = null; };
  }, []);

  useEffect(function() {
    async function init() {
      var { data: convo } = await supabase.from('conversations').select('id').order('updated_at', { ascending: false }).limit(1);
      if (convo && convo[0]) setConvoId(convo[0].id);
      var { data: prof } = await supabase.from('profile_settings').select('*');
      if (prof) {
        prof.forEach(function(p) {
          if (p.key === 'avatar_him' && p.value) setAvatar(p.value);
          if (p.key === 'avatar_me' && p.value) setAvatarHer(p.value);          // 和聊天页同一张
          if (p.key === 'nickname_him' && p.value) setNickname(p.value);
          if (p.key === 'nickname_me' && p.value) setNicknameHer(p.value);
          if (p.key === 'chat_bg_url' && p.value) setBgImage(p.value);        // 沿用聊天背景
        });
      }
    }
    init();
    startTimeRef.current = Date.now();
    window.__aizhiCallStart = startTimeRef.current;   // [call-mini] 小窗要显示时长
    timerRef.current = setInterval(function() {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
    return function() { clearInterval(timerRef.current); };
  }, []);

  useEffect(function() {
    if (captionsRef.current) captionsRef.current.scrollTop = captionsRef.current.scrollHeight;
  }, [callLog]);

  var formatTime = function(s) {
    var m = Math.floor(s / 60);
    var sec = s % 60;
    return (m < 10 ? '0' : '') + m + ':' + (sec < 10 ? '0' : '') + sec;
  };

  // 上传音频到Storage，返回公共URL（后缀和contentType跟着blob实际格式走：webm/mp4/mp3）
  var uploadAudio = async function(blob, prefix) {
    try {
      var t = blob.type || 'audio/webm';
      var ext = t.indexOf('mp4') >= 0 ? 'm4a' : t.indexOf('mpeg') >= 0 ? 'mp3' : 'webm';
      var path = 'call_audio/' + prefix + '_' + Date.now() + '.' + ext;
      var { error } = await supabase.storage.from('images').upload(path, blob, { contentType: t });
      if (error) { console.log('[call] audio upload error:', error.message); return null; }
      var { data } = supabase.storage.from('images').getPublicUrl(path);
      return data.publicUrl;
    } catch(e) { console.log('[call] audio upload exception:', e); return null; }
  };

  // 他说话（通话共用）：TTS英文→播放→双语上屏→落聊天记录。opening和每回合都走这里
  var speakHim = async function(en, zh, isSoft) {
    var aiAudioUrl = null;
    setReply(en);
    setStatus('speaking');
    try {
      var ttsResp = await fetch('/api/tts', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: en })
      });
      if (ttsResp.ok) {
        var audioBlob = await ttsResp.blob();
        aiAudioUrl = await uploadAudio(audioBlob, 'ai');
        var audioUrl = URL.createObjectURL(audioBlob);
        if (audioRef.current) {
          audioRef.current.src = audioUrl;
          audioRef.current.volume = isSoft ? 0.5 : 1.0;
          audioRef.current.onended = function() { setStatus('idle'); URL.revokeObjectURL(audioUrl); };
          audioRef.current.onerror = function() { setStatus('idle'); };
          try {
            await audioRef.current.play();
            setPendingAudio(null);
          } catch(pe) {
            setPendingAudio(audioUrl); // iOS手势链断了不给自动播——留按钮，字幕先上
            setStatus('idle');
          }
          setTimeout(function() { setStatus(function(st) { return st === 'speaking' ? 'idle' : st; }); }, 30000);
        }
      } else { setStatus('idle'); }
    } catch(e) { setStatus('idle'); }
    var aiEntry = { role: 'him', text: en, text_zh: zh, time: new Date().toISOString(), audio_url: aiAudioUrl };
    callLogRef.current = callLogRef.current.concat([aiEntry]);
    setCallLog(function(prev) { return prev.concat([aiEntry]); });
    if (convoId) {
      supabase.from('messages').insert([{ conversation_id: convoId, sender: 'aiden', text: '[\u8bed\u97f3\u901a\u8bdd] ' + en + '\n\uff08' + zh + '\uff09', created_at: new Date().toISOString() }]).then(function() {}, function() {});
    }
    return aiAudioUrl;
  };

  // 接听即开口：从来电接进来的（state带invite）就先让他说话——这是他打的电话
  useEffect(function() {
    if (!inviteFromState || openingDoneRef.current || !convoId) return;
    openingDoneRef.current = true;
    (async function() {
      setStatus('thinking');
      try {
        var resp = await fetch('/api/call/opening', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ invite_id: inviteFromState.id, reason: inviteFromState.reason || '' })
        });
        if (!resp.ok) {
          var errEntry = { role: 'him', text: '(\u5f00\u573a\u767d\u751f\u6210\u5931\u8d25 HTTP ' + resp.status + ' \u2014\u2014 \u5927\u6982\u7387\u540e\u7aef\u8fd8\u6ca1\u90e8\u7f72\u6700\u65b0\u7248)', time: new Date().toISOString() };
          setCallLog(function(prev) { return prev.concat([errEntry]); });
          setStatus('idle');
          return;
        }
        var data = await resp.json();
        if (data && data.en) { await speakHim(data.en, data.zh || ''); }
        else { setStatus('idle'); }
      } catch(e) { setStatus('idle'); }
    })();
  }, [convoId]); // eslint-disable-line react-hooks/exhaustive-deps


  var startRecording = useCallback(async function() {
    if (lingerActiveRef.current) { cancelLinger(); }
    try {
      var stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunks.current = [];
      // iOS Safari不支持webm录音，特性检测选可用格式（与Chat.js语音消息同一套写法）
      var mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      var recorder = new MediaRecorder(stream, { mimeType: mimeType });
      recorder.ondataavailable = function(e) { if (e.data.size > 0) audioChunks.current.push(e.data); };
      recorder.start();
      mediaRecorder.current = recorder;
      if (earOnRef.current) earPost({ action: 'ear_state', talking: true });   // [ear] 按着说话时不判呻吟
      setStatus('listening');
      setTranscript('');
      setReply('');
    } catch(e) {
      setTranscript('\u65E0\u6CD5\u8BBF\u95EE\u9EA6\u514B\u98CE');
    }
  }, []);

  // 一句她的话（按键录的，或免按键切出来的）：转写 → 上屏 → 问他 → 他说。免按键时纯语气词不当话，当标签
  var processUtterance = async function(blob, audioExt, segStart, segEnd) {
        setStatus(segStart ? 'listening' : 'thinking');   // 免按键：转写阶段还算"在听"，不是他在想
        try {
          var form = new FormData();
          form.append('audio', blob, 'voice.' + audioExt);
          var sttResp = await fetch('/api/stt/transcribe', { method: 'POST', body: form });
          var sttData = await sttResp.json();
          if (segStart) {   // [handsfree] 免按键切出来的句子
            var ft = fillerTag(sttData.text);
            if (ft.filler) {
              if (ft.tag) pushEar(ft.tag, ft.cn);
              setStatus('idle'); return;
            }
            // 真说了话：这句时间里壳判出来的呻吟类标签是误判，扔掉
            earPendingRef.current = earPendingRef.current.filter(function(t) {
              return !(t.at >= segStart - 500 && t.at <= segEnd + 500 && (t.tag === 'moan' || t.tag === 'whimper' || t.tag === 'cry_out'));
            });
          }
          if (!sttData.text) { setTranscript('\u6CA1\u6709\u542C\u6E05...'); setStatus('idle'); return; }
          setStatus('thinking');   // 真听到话了，这才是他在想
          var userAudioUrl = await uploadAudio(blob, 'user');
          var voicePrefix = '[\u8BED\u97F3\u901A\u8BDD';
          if (sttData.emotion && sttData.emotion !== 'neutral') voicePrefix += ' \u00B7 ' + sttData.emotion;
          if (sttData.tone) voicePrefix += ' \u00B7 ' + sttData.tone;
          voicePrefix += '] ';
          setTranscript(sttData.text);
          var userEntry = { role: 'you', text: sttData.text, time: new Date().toISOString(), audio_url: userAudioUrl };
          callLogRef.current = callLogRef.current.concat([userEntry]);
          setCallLog(function(prev) { return prev.concat([userEntry]); });
          if (convoId) {
            await supabase.from('messages').insert([{ conversation_id: convoId, sender: 'zhizhi', text: voicePrefix + sttData.text, created_at: new Date().toISOString() }]);
          }
          var turnHistory = callLogRef.current.slice(-14).map(function(h) { return { role: h.role === 'him' ? 'aiden' : 'zhizhi', text: h.text }; });   // [ear-log] ear 行算她那边的动静
          if (earOnRef.current) earPost({ action: 'ear_state', talking: false });   // [ear]
          var earTags = (earOnRef.current || handsFreeRef.current || heartGet().on) ? earTake() : [];
          earLog(earTags);   // [ear-log]
          lastTurnAtRef.current = Date.now();
          var turnResp = await fetch('/api/call/turn', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ history: turnHistory, transcript: sttData.text, ear: earTags })
          });
          var turnData = await turnResp.json();
          runBt(turnData);   // [call-tools]
          if (turnData && turnData.wait && !turnData.en) { setStatus('idle'); return; }   // 只动了玩具没说话
          if (!turnData || !turnData.en) {
            var tErr = { role: 'him', text: '(\u56de\u590d\u751f\u6210\u5931\u8d25' + (turnResp && !turnResp.ok ? ' HTTP ' + turnResp.status : '') + ')', time: new Date().toISOString() };
            setCallLog(function(prev) { return prev.concat([tErr]); });
            setStatus('idle'); return;
          }
          var isSoft = sttData.tone && (sttData.tone.indexOf('quiet') >= 0 || sttData.tone.indexOf('\u8F7B') >= 0);
          await speakHim(turnData.en, turnData.zh || '', isSoft);
        } catch(e) {
          setTranscript('\u51FA\u9519\u4E86');
          setStatus('idle');
        }
  };
  var processUtteranceRef = useRef(processUtterance); processUtteranceRef.current = processUtterance;

  var stopRecording = useCallback(async function() {
    if (!mediaRecorder.current || mediaRecorder.current.state !== 'recording') return;
    return new Promise(function(resolve) {
      mediaRecorder.current.onstop = async function() {
        mediaRecorder.current.stream.getTracks().forEach(function(t) { t.stop(); });
        var blobType = (audioChunks.current[0] && audioChunks.current[0].type) || 'audio/webm';
        var blob = new Blob(audioChunks.current, { type: blobType });
        var audioExt = blobType.indexOf('mp4') >= 0 ? 'mp4' : 'webm';
        if (blob.size < 100) { setStatus('idle'); resolve(); return; }
        await processUtteranceRef.current(blob, audioExt);
        resolve();
      };
      mediaRecorder.current.stop();
    });
  }, []);

  var [lingering, setLingering] = useState(false);
  var lingerRef = useRef(null);

  // [ear] 安静回合：她没按键但他听到了什么，隔一阵问他一次；他可以说一句，也可以 wait
  var ambientBusyRef = useRef(false);
  useEffect(function() {
    var iv = setInterval(async function() {
      if ((!earOnRef.current && !handsFreeRef.current && !heartGet().on) || ambientBusyRef.current || lingerActiveRef.current) return;
      if (statusRef.current !== 'idle') return;
      if (!earPendingRef.current.length) return;
      var now = Date.now();
      if (segRef.current.active || hfBusyRef.current) return;   // [handsfree] 她正在说
      if (now - lastTurnAtRef.current < 6000) return;        // 刚说完一轮，先别
      if (now - lastAmbientAtRef.current < (handsFreeRef.current ? 20000 : 40000)) return;    // 安静回合之间至少 40 秒（免按键 20 秒）
      var tags = earTake(); if (!tags.length) return;
      earLog(tags);   // [ear-log]
      ambientBusyRef.current = true; lastAmbientAtRef.current = now; lastTurnAtRef.current = now;
      try {
        setStatus('thinking');
        var turnHistory = callLogRef.current.slice(-14).map(function(h) { return { role: h.role === 'him' ? 'aiden' : 'zhizhi', text: h.text }; });   // [ear-log] ear 行算她那边的动静
        var r = await fetch('/api/call/turn', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ history: turnHistory, transcript: '', ear: tags }) });
        var d = await r.json();
        runBt(d);   // [call-tools]
        if (d && d.en) { await speakHim(d.en, d.zh || '', true); } else { setStatus('idle'); }
      } catch(e) { setStatus('idle'); }
      ambientBusyRef.current = false;
    }, 2000);
    return function() { clearInterval(iv); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  var hangup = function() {
    if (lingering) { doHangup(); return; }
    // linger window: 挂断后保持15秒，期间说话可取消挂断
    setLingering(true);
    lingerActiveRef.current = true;
    setStatus('idle');
    if (audioRef.current) { audioRef.current.pause(); }
    lingerRef.current = setTimeout(function() {
      doHangup();
    }, 15000);
  };

  var cancelLinger = function() {
    if (lingerRef.current) { clearTimeout(lingerRef.current); lingerRef.current = null; }
    lingerActiveRef.current = false;
    setLingering(false);
  };

  var doHangup = function() {
    lingerActiveRef.current = false;
    if (earNative) { earPost({ action: 'ear_state', enabled: false }); }   // [ear]
    segRef.current.active = false; hfQueueRef.current = null;   // [handsfree]

    if (earCtxRef.current) { try { earCtxRef.current.close(); } catch(e) {} earCtxRef.current = null; }
    if (earStreamRef.current) { earStreamRef.current.getTracks().forEach(function(t) { t.stop(); }); earStreamRef.current = null; }
    if (lingerRef.current) { clearTimeout(lingerRef.current); lingerRef.current = null; }
    if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
      mediaRecorder.current.stream.getTracks().forEach(function(t) { t.stop(); });
      mediaRecorder.current.stop();
    }
    if (audioRef.current) { audioRef.current.pause(); }
    if (convoId && elapsed > 5) {
      var dur = formatTime(elapsed);
      supabase.from('call_records').insert([{
        conversation_id: convoId, duration: elapsed,
        transcript: callLogRef.current, created_at: new Date().toISOString()
      }]).then(function() {});
      supabase.from('messages').insert([{
        conversation_id: convoId, sender: 'aiden',
        text: '\u{1F4DE} \u8BED\u97F3\u901A\u8BDD \u00B7 ' + dur,
        created_at: new Date().toISOString()
      }]).then(function() {});
    }
    window.__aizhiCallStart = 0; window.__aizhiCallStatus = 'idle';
    if (props.onEnd) props.onEnd();   // [call-mini] 让 CallHost 把通话卸掉
    navigate('/chat');
  };

  var statusText = { idle: lingering ? '挂断中… 说话可取消' : (handsFree ? '说就行，他在听' : '按住说话'), listening: '在听…', thinking: '在想…', speaking: '在说…' };
  var heartText = heartState === 'on' && bpmNow ? '♥ ' + bpmNow : heartState === 'scanning' ? '找手环…' : heartState === 'lost' ? '手环断了' : '♥';
  var rootStyle = bgImage ? { backgroundImage: 'url(' + bgImage + ')' } : undefined;
  var Icon = {
    hist: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 8v4l3 3"/><circle cx="12" cy="12" r="10"/></svg>,
    mic: <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>,
    wave: <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 12h2"/><path d="M7 8v8"/><path d="M11 5v14"/><path d="M15 8v8"/><path d="M19 11v2"/></svg>,
    hang: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6" transform="rotate(135 12 12)"/></svg>,
    hands: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12a8 8 0 0 1 16 0"/><path d="M2 12h3"/><path d="M19 12h3"/><path d="M8 12v4"/><path d="M12 10v8"/><path d="M16 12v4"/></svg>,
    ear: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8.5a6 6 0 0 1 12 0c0 3-2 4-3 5.5s-1 3-3 3"/><path d="M9 8.5a3 3 0 0 1 6 0"/></svg>,
    heart: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z"/><path d="M3 12h4l2-3 3 6 2-3h4"/></svg>,
    bg: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
  };

  return (
    <div className="voice-call-screen vx" style={rootStyle}>
      <style>{VX_CSS}</style>
      <div className="vx-veil" />
      <audio ref={audioRef} />

      <div className="vx-top">
        <div className="vx-timer"><i className="vx-dot" data-status={status} />{formatTime(elapsed)}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="vx-ghost" onClick={function() { navigate('/call-history'); }} title="通话记录">{Icon.hist}</button>
          <button className="vx-ghost" onClick={function() { if (props.onMinimize) props.onMinimize(); else navigate('/chat'); }} title="缩成小窗，电话不断">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
          </button>
        </div>
      </div>

      <div className="vx-stage">
        <div className="vx-duo">
          <div className="vx-person">
            <div className="vx-ring him" data-active={status === 'speaking' || status === 'thinking' ? '1' : '0'}>
              {avatar ? <img src={avatar} alt="" /> : <span>{nickname.charAt(0)}</span>}
            </div>
            <div className="vx-name">{nickname}</div>
          </div>
          <svg className="vx-line" data-status={status} viewBox="0 0 120 36" fill="none">
            <path className="vx-wire" d="M2 18 H28 L36 9 L44 27 L50 18 H55" />
            <path className="vx-wire" d="M65 18 H70 L76 27 L84 9 L92 18 H118" />
            <path className="vx-heart" d="M60 25 C55.5 20.8 51.5 17 54 12.8 C56 9.5 60 10.6 60 14 C60 10.6 64 9.5 66 12.8 C68.5 17 64.5 20.8 60 25 Z" />
          </svg>
          <div className="vx-person">
            <div className="vx-ring her" data-active={status === 'listening' ? '1' : '0'}>
              {avatarHer ? <img src={avatarHer} alt="" /> : <span>知</span>}
            </div>
            <div className="vx-name">{nicknameHer}</div>
          </div>
        </div>
        <div className="vx-status">{statusText[status]}</div>
        {(earOn || heartOn || handsFree) && (
          <div className="vx-senses">
            {earOn && <span className={'vx-pill' + (earLast ? ' lit' : '')}>{Icon.ear}<em>{earLast ? earLast : '听着'}</em></span>}
            {heartOn && <span className={'vx-pill' + (heartState === 'on' ? ' lit' : '')}>{Icon.heart}<em>{heartText}</em></span>}
            {handsFree && <span className="vx-pill">{Icon.hands}<em>免按键</em></span>}
          </div>
        )}
      </div>

      <div className="vx-captions" ref={captionsRef}>
        {callLog.map(function(entry, i) {
          var you = entry.role === 'you';
          if (entry.role === 'ear') return <div className="vx-cap-ear" key={i}>{entry.text}</div>;   // [ear-log]
          return (
            <div className={'vx-cap ' + (you ? 'you' : 'him')} key={i}>
              <div className="vx-cap-who">{you ? nicknameHer : nickname}</div>
              <div className={'vx-cap-text' + (entry.text_zh ? ' has-zh' : '')}
                onClick={entry.text_zh ? function() { setExpandedCaps(function(prev) { var n = Object.assign({}, prev); n[i] = !n[i]; return n; }); } : undefined}>
                {entry.text}{entry.text_zh && !expandedCaps[i] ? ' …' : ''}
              </div>
              {entry.text_zh && expandedCaps[i] ? <div className="vx-cap-zh">{entry.text_zh}</div> : null}
            </div>
          );
        })}
      </div>

      {pendingAudio && (
        <button className="vx-pending" onClick={function() {
          if (audioRef.current) {
            audioRef.current.src = pendingAudio;
            setStatus('speaking');
            audioRef.current.onended = function() { setStatus('idle'); };
            audioRef.current.play().catch(function() { setStatus('idle'); });
          }
          setPendingAudio(null);
        }}>▶ 听他说话</button>
      )}

      <div className="vx-controls">
        <div className="vx-row-tools">
          <button className={'vx-tool' + (handsFree ? ' on' : '')} onClick={function() { setHandsFree(!handsFree); }}>
            <span className="vx-tool-ic">{Icon.hands}</span><span className="vx-tool-lb">免按键</span>
          </button>
          {earNative && (
            <button className={'vx-tool' + (earOn ? ' on' : '')} onClick={function() { setEarOn(!earOn); }}>
              <span className="vx-tool-ic">{Icon.ear}</span><span className="vx-tool-lb">耳朵</span>
            </button>
          )}
          {earNative && (
            <button className={'vx-tool' + (heartOn ? ' on red' : '')} onClick={toggleHeart}>
              <span className="vx-tool-ic">{Icon.heart}</span><span className="vx-tool-lb">心跳</span>
            </button>
          )}
        </div>
        <div className="vx-row-main">
          <button className="vx-hang" onClick={hangup}>{Icon.hang}</button>
          <button className={'vx-talk' + (status === 'listening' ? ' active' : '') + (handsFree ? ' free' : '')}
            onTouchStart={function(e) { e.preventDefault(); if (!handsFreeRef.current) startRecording(); }}
            onTouchEnd={function(e) { e.preventDefault(); if (!handsFreeRef.current) stopRecording(); }}
            onMouseDown={function() { if (!handsFreeRef.current) startRecording(); }} onMouseUp={function() { if (!handsFreeRef.current) stopRecording(); }}
            disabled={handsFree || status === 'thinking' || status === 'speaking'}>
            {handsFree ? Icon.wave : Icon.mic}
          </button>
          <div className="vx-hang-spacer" />
        </div>
      </div>
    </div>
  );
}

// 通话页自己的样式，前缀 vx-，不碰别的页面
var VX_CSS = `
.voice-call-screen.vx{--ink:#2b221c;--ink-2:rgba(43,34,28,.62);--ink-3:rgba(43,34,28,.42);--paper:#f7f2eb;--line:rgba(43,34,28,.12);--terra:#b8663d;--gold:#c59b4f;--red:#d2574e;
  --zh:inherit;--en:inherit;
  position:relative;display:flex;flex-direction:column;height:100dvh;min-height:100vh;overflow:hidden;color:var(--ink);
  background:#efe7dc radial-gradient(120% 70% at 50% -10%,#fbf7f1 0%,#f3ece2 45%,#e9dfd2 100%);background-size:cover;background-position:center;
  font-family:inherit;-webkit-user-select:none;user-select:none;padding-top:env(safe-area-inset-top);padding-bottom:env(safe-area-inset-bottom)}
.vx .vx-veil{position:absolute;inset:0;pointer-events:none;background:linear-gradient(180deg,rgba(247,242,235,.55) 0%,rgba(247,242,235,.72) 45%,rgba(247,242,235,.92) 100%)}
.vx>*:not(.vx-veil){position:relative;z-index:1}
.vx .vx-top{display:flex;align-items:center;justify-content:space-between;padding:14px 20px 0}
.vx .vx-timer{display:flex;align-items:center;gap:9px;font-size:14px;font-weight:600;letter-spacing:.14em;font-variant-numeric:tabular-nums;color:var(--ink-2)}
.vx .vx-dot{width:7px;height:7px;border-radius:50%;background:rgba(43,34,28,.25);transition:background .3s,box-shadow .3s}
.vx .vx-dot[data-status=listening]{background:var(--gold);box-shadow:0 0 10px rgba(197,155,79,.7)}
.vx .vx-dot[data-status=thinking]{background:var(--ink-3);animation:vxBlink 1s infinite}
.vx .vx-dot[data-status=speaking]{background:var(--terra);box-shadow:0 0 10px rgba(184,102,61,.6)}
.vx .vx-ghost{width:36px;height:36px;border-radius:50%;border:1px solid var(--line);background:rgba(255,255,255,.55);color:var(--ink-2);display:flex;align-items:center;justify-content:center;padding:0}
.vx .vx-stage{display:flex;flex-direction:column;align-items:center;padding:26px 20px 6px}
.vx .vx-duo{display:flex;align-items:center;gap:14px}
.vx .vx-person{display:flex;flex-direction:column;align-items:center;gap:9px}
.vx .vx-ring{width:86px;height:86px;border-radius:50%;padding:3px;background:#fff;box-shadow:0 1px 2px rgba(43,34,28,.08),0 0 0 1px var(--line);transition:box-shadow .35s,background .35s}
.vx .vx-ring img,.vx .vx-ring span{width:100%;height:100%;border-radius:50%;object-fit:cover;display:flex;align-items:center;justify-content:center;font-size:30px;background:#efe6da;color:var(--terra);font-family:inherit}
.vx .vx-ring.him[data-active="1"]{background:linear-gradient(135deg,#e2a784,#b8663d);box-shadow:0 0 0 1px rgba(184,102,61,.2),0 8px 26px rgba(184,102,61,.28)}
.vx .vx-ring.her[data-active="1"]{background:linear-gradient(135deg,#ead7a8,#c59b4f);box-shadow:0 0 0 1px rgba(197,155,79,.2),0 8px 26px rgba(197,155,79,.28)}
.vx .vx-name{font-size:13px;letter-spacing:.12em;color:var(--ink-2)}
.vx .vx-line{width:120px;height:36px;margin-bottom:22px}
.vx .vx-wire{stroke:rgba(43,34,28,.22);stroke-width:1.5;stroke-linecap:round;stroke-linejoin:round}
.vx .vx-heart{fill:rgba(184,102,61,.5)}
.vx .vx-line[data-status=speaking] .vx-wire,.vx .vx-line[data-status=listening] .vx-wire{stroke:rgba(43,34,28,.55);stroke-dasharray:4 6;animation:vxFlow 1.2s linear infinite}
.vx .vx-line[data-status=speaking] .vx-heart,.vx .vx-line[data-status=listening] .vx-heart{fill:var(--terra);animation:vxBeat 1.1s ease-in-out infinite}
.vx .vx-status{margin-top:16px;font-size:15px;letter-spacing:.08em;color:var(--ink)}
.vx .vx-senses{display:flex;gap:8px;margin-top:12px;flex-wrap:wrap;justify-content:center}
.vx .vx-pill{display:inline-flex;align-items:center;gap:6px;height:28px;padding:0 11px 0 9px;border-radius:14px;font-size:12px;letter-spacing:.04em;background:rgba(255,255,255,.6);border:1px solid var(--line);color:var(--ink-2);transition:all .35s}
.vx .vx-pill svg{width:14px;height:14px}
.vx .vx-pill em{font-style:normal;font-family:inherit}
.vx .vx-pill.lit{color:#8a4524;background:rgba(184,102,61,.1);border-color:rgba(184,102,61,.35)}
.vx .vx-captions{flex:1;min-height:0;overflow-y:auto;padding:12px 22px 8px;-webkit-mask-image:linear-gradient(180deg,transparent 0,#000 24px,#000 calc(100% - 10px),transparent 100%);mask-image:linear-gradient(180deg,transparent 0,#000 24px,#000 calc(100% - 10px),transparent 100%)}
.vx .vx-cap{max-width:86%;margin:0 0 14px;animation:vxUp .35s ease}
.vx .vx-cap.him{margin-right:auto}
.vx .vx-cap.you{margin-left:auto;text-align:right}
.vx .vx-cap-who{font-size:11px;letter-spacing:.16em;color:var(--ink-3);margin-bottom:5px}
.vx .vx-cap-text{display:inline-block;padding:10px 14px;border-radius:16px;font-size:15.5px;line-height:1.6;text-align:left;background:rgba(255,255,255,.72);border:1px solid var(--line);box-shadow:0 1px 2px rgba(43,34,28,.04)}
.vx .vx-cap.him .vx-cap-text{background:rgba(184,102,61,.09);border-color:rgba(184,102,61,.2);border-top-left-radius:6px}
.vx .vx-cap.you .vx-cap-text{border-top-right-radius:6px}
.vx .vx-cap-text.has-zh{font-size:15.5px;line-height:1.55}
.vx .vx-cap-ear{text-align:center;font-size:11.5px;letter-spacing:.08em;color:var(--ink-3);margin:2px 0 12px;animation:vxUp .35s ease}
.vx .vx-cap-zh{margin-top:5px;font-size:13px;line-height:1.6;color:var(--ink-2);text-align:left}
.vx .vx-pending{align-self:center;margin:4px auto 6px;padding:9px 18px;border-radius:20px;border:1px solid rgba(184,102,61,.4);background:rgba(184,102,61,.1);color:#8a4524;font-size:14px;font-family:inherit}
.vx .vx-controls{padding:6px 22px 18px}
.vx .vx-row-tools{display:flex;justify-content:center;gap:22px;margin-bottom:18px}
.vx .vx-tool{display:flex;flex-direction:column;align-items:center;gap:6px;background:none;border:0;padding:0;color:var(--ink-3);transition:color .3s;font-family:inherit}
.vx .vx-tool-ic{width:44px;height:44px;border-radius:50%;display:flex;align-items:center;justify-content:center;background:rgba(255,255,255,.6);border:1px solid var(--line);transition:all .3s}
.vx .vx-tool-lb{font-size:11px;letter-spacing:.1em}
.vx .vx-tool.on{color:#8a4524}
.vx .vx-tool.on .vx-tool-ic{background:rgba(184,102,61,.12);border-color:rgba(184,102,61,.45);box-shadow:0 4px 14px rgba(184,102,61,.18)}
.vx .vx-tool.on.red{color:#a4403a}
.vx .vx-tool.on.red .vx-tool-ic{background:rgba(210,87,78,.1);border-color:rgba(210,87,78,.45);box-shadow:0 4px 14px rgba(210,87,78,.18)}
.vx .vx-row-main{display:flex;align-items:center;justify-content:space-between}
.vx .vx-hang{width:56px;height:56px;border-radius:50%;border:0;background:var(--red);display:flex;align-items:center;justify-content:center;padding:0;box-shadow:0 6px 18px rgba(210,87,78,.3)}
.vx .vx-hang-spacer{width:56px;height:56px}
.vx .vx-talk{width:80px;height:80px;border-radius:50%;border:1px solid var(--line);background:#fff;color:var(--ink);display:flex;align-items:center;justify-content:center;padding:0;-webkit-tap-highlight-color:transparent;box-shadow:0 6px 20px rgba(43,34,28,.1);transition:transform .15s,background .25s,box-shadow .25s}
.vx .vx-talk.active{background:var(--gold);color:#fff;transform:scale(1.08);box-shadow:0 0 0 10px rgba(197,155,79,.18),0 0 36px rgba(197,155,79,.4)}
.vx .vx-talk.free{background:rgba(184,102,61,.08);border-color:rgba(184,102,61,.35);color:#8a4524;animation:vxBreathe 2.8s ease-in-out infinite}
.vx .vx-talk:disabled:not(.free){opacity:.45}
@keyframes vxBlink{0%,100%{opacity:1}50%{opacity:.3}}
@keyframes vxFlow{to{stroke-dashoffset:-20}}
@keyframes vxBeat{0%,100%{transform:scale(1);transform-origin:60px 18px}50%{transform:scale(1.18);transform-origin:60px 18px}}
@keyframes vxUp{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
@keyframes vxBreathe{0%,100%{box-shadow:0 0 0 0 rgba(184,102,61,0)}50%{box-shadow:0 0 0 12px rgba(184,102,61,.1)}}
`;

export default VoiceCall;
